import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { verbaliApi } from '../services/api'
import SignatureCanvasPad from '../components/timsurvey/SignatureCanvasPad'

interface BeneItem {
  descrizione: string
  seriale: string
  quantita: number
  unita: string
  data_consegna: string
}

interface VerificaData {
  valid: boolean
  tecnico: { nome: string; cognome: string }
  assegnazione: { id: number; data: string }
  beni: BeneItem[]
}

type UIState = 'loading' | 'errore' | 'gia_firmata' | 'scaduto' | 'pronto' | 'invio' | 'successo'

export default function FirmaVerbale() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [uiState, setUiState] = useState<UIState>('loading')
  const [dati, setDati] = useState<VerificaData | null>(null)
  const [errorMsg, setErrorMsg] = useState('')
  const [dataFirma, setDataFirma] = useState<string | null>(null)
  const [countdown, setCountdown] = useState<number | null>(null)

  useEffect(() => {
    if (!token) { setUiState('errore'); setErrorMsg('Token mancante'); return }
    verbaliApi.verificaToken(token)
      .then(r => {
        setDati(r.data)
        setUiState('pronto')
      })
      .catch(err => {
        const detail = err.response?.data?.detail
        if (typeof detail === 'object' && detail?.code === 'gia_firmata') {
          setDataFirma(detail.data_firma ? new Date(detail.data_firma).toLocaleString('it-IT') : null)
          setUiState('gia_firmata')
        } else if (typeof detail === 'object' && detail?.code === 'scaduto') {
          setUiState('scaduto')
        } else {
          setErrorMsg(typeof detail === 'string' ? detail : 'Link non valido o scaduto')
          setUiState('errore')
        }
      })
  }, [token])

  useEffect(() => {
    if (uiState !== 'successo') return
    const isLoggedIn = !!localStorage.getItem('xsophia_token')
    if (!isLoggedIn) return
    setCountdown(4)
    const iv = setInterval(() => {
      setCountdown(prev => {
        if (prev === null || prev <= 1) { clearInterval(iv); navigate('/'); return null }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(iv)
  }, [uiState, navigate])

  const handleFirma = async (blob: Blob) => {
    if (!token) return
    setUiState('invio')
    try {
      await verbaliApi.firmaVerbale(token, blob)
      setUiState('successo')
    } catch (err: any) {
      const msg = err.response?.data?.detail || 'Errore durante il salvataggio della firma'
      setErrorMsg(typeof msg === 'string' ? msg : 'Errore durante il salvataggio')
      setUiState('errore')
    }
  }

  const fmtData = (d: string) => {
    try { return new Date(d).toLocaleDateString('it-IT') } catch { return d }
  }

  return (
    <div style={{
      minHeight: '100vh', background: '#f1f5f9',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '24px 16px',
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
        maxWidth: 580, width: '100%', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ background: '#1E40AF', padding: '20px 28px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div>
              <div style={{ color: '#fff', fontSize: 18, fontWeight: 700 }}>XSophia</div>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                X-Sophia 360
              </div>
            </div>
          </div>
          <div style={{ color: '#fff', fontSize: 14, marginTop: 10, fontWeight: 600, opacity: 0.9 }}>
            Verbale di Consegna Beni e Attrezzature
          </div>
        </div>

        <div style={{ padding: '24px 28px' }}>

          {/* Loading */}
          {uiState === 'loading' && (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#6b7280' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
              <div>Verifica del link in corso...</div>
            </div>
          )}

          {/* Errore generico */}
          {uiState === 'errore' && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>❌</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
                Link non valido
              </div>
              <div style={{ color: '#6b7280', fontSize: 14 }}>{errorMsg}</div>
            </div>
          )}

          {/* Già firmato */}
          {uiState === 'gia_firmata' && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
                Verbale già firmato
              </div>
              {dataFirma && (
                <div style={{ color: '#6b7280', fontSize: 14 }}>
                  Firma acquisita il {dataFirma}
                </div>
              )}
              <div style={{ color: '#6b7280', fontSize: 13, marginTop: 12 }}>
                Non è necessario eseguire ulteriori azioni.
              </div>
            </div>
          )}

          {/* Token scaduto */}
          {uiState === 'scaduto' && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>⏰</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
                Link scaduto
              </div>
              <div style={{ color: '#6b7280', fontSize: 14 }}>
                Il link di firma è scaduto (validità 30 giorni).<br />
                Contatta il responsabile X-Sophia 360 per ricevere un nuovo link.
              </div>
            </div>
          )}

          {/* Pronto per firma */}
          {uiState === 'pronto' && dati && (
            <>
              {/* Dati tecnico */}
              <div style={{
                background: '#f8fafc', border: '1px solid #e5e7eb',
                borderRadius: 10, padding: '14px 16px', marginBottom: 20,
              }}>
                <div style={{ fontSize: 12, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                  Tecnico
                </div>
                <div style={{ fontSize: 17, fontWeight: 700, color: '#111827' }}>
                  {dati.tecnico.nome} {dati.tecnico.cognome}
                </div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                  Data assegnazione: <strong>{fmtData(dati.assegnazione.data)}</strong>
                </div>
              </div>

              {/* Lista beni */}
              <div style={{ marginBottom: 24 }}>
                <div style={{
                  fontSize: 12, fontWeight: 700, color: '#374151',
                  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10,
                }}>
                  Beni assegnati ({dati.beni.length})
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {dati.beni.map((b, i) => (
                    <div key={i} style={{
                      background: '#f8fafc', border: '1px solid #e5e7eb',
                      borderRadius: 8, padding: '10px 14px',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                      <div>
                        <div style={{ fontWeight: 600, color: '#111827', fontSize: 14 }}>
                          {b.descrizione}
                        </div>
                        {b.seriale && (
                          <div style={{ fontSize: 12, color: '#6b7280', fontFamily: 'monospace', marginTop: 2 }}>
                            SN: {b.seriale}
                          </div>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap', marginLeft: 8 }}>
                        {Number(b.quantita) % 1 === 0 ? Math.round(b.quantita) : b.quantita} {b.unita}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Dichiarazione */}
              <div style={{
                background: '#fefce8', border: '1px solid #fbbf24',
                borderRadius: 8, padding: '12px 14px', marginBottom: 24,
                fontSize: 13, color: '#92400e', lineHeight: 1.6,
              }}>
                Firmando dichiaro di aver ricevuto i beni sopra elencati e di essere responsabile
                della loro custodia e manutenzione. I beni saranno restituiti al termine del
                rapporto di lavoro o su richiesta aziendale.
              </div>

              {/* Canvas firma */}
              <div style={{ marginBottom: 8 }}>
                <div style={{
                  fontSize: 13, fontWeight: 700, color: '#374151',
                  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10,
                }}>
                  Firma digitale
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
                  Disegna la tua firma nel riquadro sottostante
                </div>
                <SignatureCanvasPad onConfirm={handleFirma} />
              </div>
            </>
          )}

          {/* Invio in corso */}
          {uiState === 'invio' && (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#6b7280' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
              <div style={{ fontWeight: 600, fontSize: 15 }}>Salvataggio firma in corso...</div>
              <div style={{ fontSize: 13, marginTop: 6 }}>Attendere prego</div>
            </div>
          )}

          {/* Successo */}
          {uiState === 'successo' && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
                Verbale firmato con successo!
              </div>
              <div style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.7 }}>
                La tua firma è stata acquisita e il verbale è stato archiviato.
              </div>
              <div style={{
                marginTop: 16, background: '#f0fdf4', border: '1px solid #bbf7d0',
                borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#166534',
              }}>
                Il responsabile X-Sophia 360 riceverà notifica dell'avvenuta firma.
              </div>
              <button
                onClick={() => navigate('/')}
                style={{
                  marginTop: 24, padding: '12px 32px', borderRadius: 8,
                  background: '#1E40AF', color: '#fff', border: 'none',
                  fontSize: 14, fontWeight: 700, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                }}
              >
                ← Torna all'app
                {countdown !== null && (
                  <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: 10, padding: '1px 7px', fontSize: 12 }}>
                    {countdown}s
                  </span>
                )}
              </button>
              {countdown === null && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#9ca3af' }}>
                  oppure chiudi questa pagina
                </div>
              )}
            </div>
          )}

        </div>

        {/* Footer */}
        <div style={{
          borderTop: '1px solid #f3f4f6', padding: '12px 28px',
          fontSize: 11, color: '#9ca3af', textAlign: 'center',
        }}>
          X-Sophia 360 — Sistema di gestione interventi
        </div>
      </div>
    </div>
  )
}
